import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import carlImage from '../assets/images/carl.jpg.jpg';
import { updateMetaTags, addStructuredData } from '../utils/seo';

const AboutUs: React.FC = () => {
  useEffect(() => {
    // Update meta tags for the About Us page
    updateMetaTags({
      title: 'About Explanatum | Our Story and Mission',
      description: 'Learn about Explanatum, our mission to help entrepreneurs turn ideas into successful businesses, and meet our founder Carl Harrison.',
      ogTitle: 'About Explanatum | Our Story and Mission',
      ogDescription: 'Founded in 2025, Explanatum was created to democratize entrepreneurship by leveraging AI to help entrepreneurs succeed.',
      ogUrl: typeof window !== 'undefined' ? window.location.href : 'https://explanatum.co.uk/about-us',
      twitterUrl: typeof window !== 'undefined' ? window.location.href : 'https://explanatum.co.uk/about-us',
      ogImage: 'https://explanatum.co.uk/about-team.jpg',
      canonicalUrl: 'https://explanatum.co.uk/about-us'
    });
    
    // Add structured data for organization
    addStructuredData({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': 'Explanatum Limited',
      'url': 'https://explanatum.co.uk',
      'logo': 'https://explanatum.co.uk/logo.png',
      'description': 'We help entrepreneurs turn their ideas into successful businesses using AI-powered tools.',
      'foundingDate': '2025',
      'founder': {
        '@type': 'Person',
        'name': 'Carl Harrison',
        'jobTitle': 'Founder & CEO'
      }
    }, 'about-structured-data');
  }, []);

  const founder = {
    name: 'Carl Harrison',
    role: 'Founder & CEO of Explanatum Limited',
    bio: `Hi, I'm Carl Harrison—technology has been my playground for over 40 years, starting with my trusty Commodore VIC-20. From those nostalgic days of tinkering to designing robust networks, server, and storage solutions for UK Corporates and the Public Sector, I've had the privilege of shaping the tech landscape. Along the way, I've also led powerhouse sales teams to game-changing wins.

Now, I'm channeling my experience into helping others grow their personal incomes and stay ahead of the curve with the latest in AI advancements.

As Founder & CEO of Explanatum Limited, I've created a platform that brings business ideation, planning, and growth within reach for aspiring entrepreneurs and small business owners.`,
  };

  return (
    <div className="bg-white">
      {/* Hero section */}
      <div className="relative bg-gradient-to-r from-blue-50 to-gray-50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
              About Us
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-500">
              We're on a mission to help entrepreneurs turn their ideas into successful businesses.
            </p>
          </div>
        </div>
      </div>

      {/* Our Story section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative lg:grid lg:grid-cols-3 lg:gap-x-8">
            <div className="lg:col-span-1">
              <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                Our Story
              </h2>
            </div>
            <div className="mt-6 lg:mt-0 lg:col-span-2">
              <p className="text-lg text-gray-500">
                Founded in 2025, our platform was born from a simple observation: starting a business is hard, but it doesn't have to be. Many brilliant ideas never see the light of day because entrepreneurs lack the tools, knowledge, or confidence to bring them to life.
              </p>
              <p className="mt-4 text-lg text-gray-500">
                We created this platform to democratize entrepreneurship by leveraging the power of AI to generate tailored business ideas, create comprehensive business plans, and provide the marketing tools needed to succeed.
              </p>
              <p className="mt-4 text-lg text-gray-500">
                Our team combines expertise in entrepreneurship, artificial intelligence, and business development to create a platform that supports founders at every stage of their journey.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Mission section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Our Mission
            </h2>
            <p className="mt-4 max-w-3xl mx-auto text-xl text-gray-500">
              To empower the next generation of entrepreneurs with AI-powered tools and resources that turn ideas into thriving businesses.
            </p>
          </div>

          <div className="mt-12">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              <div className="pt-6">
                <div className="flow-root bg-white rounded-lg shadow-lg px-6 pb-8">
                  <div className="-mt-6">
                    <div>
                      <span className="inline-flex items-center justify-center p-3 bg-blue-500 rounded-md shadow-lg">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </span>
                    </div>
                    <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Idea Generation</h3>
                    <p className="mt-5 text-base text-gray-500">
                      We help entrepreneurs discover viable business ideas tailored to their skills, interests, and resources.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <div className="flow-root bg-white rounded-lg shadow-lg px-6 pb-8">
                  <div className="-mt-6">
                    <div>
                      <span className="inline-flex items-center justify-center p-3 bg-blue-500 rounded-md shadow-lg">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </span>
                    </div>
                    <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Business Planning</h3>
                    <p className="mt-5 text-base text-gray-500">
                      We provide AI-powered tools to create comprehensive business plans with market analysis and financial projections.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <div className="flow-root bg-white rounded-lg shadow-lg px-6 pb-8">
                  <div className="-mt-6">
                    <div>
                      <span className="inline-flex items-center justify-center p-3 bg-blue-500 rounded-md shadow-lg">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                      </span>
                    </div>
                    <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Marketing & Growth</h3>
                    <p className="mt-5 text-base text-gray-500">
                      We offer a suite of marketing tools to help businesses build their brand, reach customers, and grow their audience.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team section */}
      <div className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            <div className="space-y-5 sm:space-y-4 text-center">
              <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl text-center">Meet the Founder</h2>
              <p className="text-xl text-gray-500 text-center">Our founder is dedicated to helping new business owners succeed.</p>
            </div>
            
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-lg shadow-sm p-8 border border-gray-100">
                <div className="text-center mb-8">
                  <div className="w-64 h-64 rounded-full overflow-hidden mx-auto mb-6 bg-blue-100 flex items-center justify-center">
                    <img 
                      src={carlImage} 
                      alt="Carl Harrison" 
                      className="w-full h-full object-cover object-top" 
                      style={{ objectPosition: '50% 30%' }}
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Carl Harrison</h3>
                  <p className="text-lg font-medium text-blue-600">Founder & CEO of Explanatum</p>
                </div>
                <div className="prose prose-lg max-w-none">
                  {founder.bio.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="text-gray-700 mb-4">{paragraph}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA section */}
      <div className="bg-blue-600">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to start your business?</span>
            <span className="block text-blue-200">Generate your first idea today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                to="/#pricing"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 transition-all duration-200 ease-in-out"
              >
                Get Started
              </Link>
            </div>
            <div className="ml-3 inline-flex rounded-md shadow">
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-500 hover:bg-blue-400 transition-all duration-200 ease-in-out"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
